import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 mb-3">
            {article.category}
          </span>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-white/80 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-white/60">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-2">
            {article.category}
          </span>
          <h3 className="font-heading text-base font-bold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-2">
          {article.category}
        </span>
        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.date}</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}